import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, Euro, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-468" data-mp="30n548ycxtjb5">
      <div className="flex items-center justify-between" data-id="element-469" data-mp="30jzctcgu65c9">
        <label className="text-sm font-medium text-slate-600" data-id="element-470" data-mp="2mnvlps19ed40">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-471" data-mp="2rlx5kf6ieggj" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-472" data-mp="3jlt4tyyc1fjl" />
    </div>;
}
type InfoKey = 'master' | 'localization' | 'insights' | null;
interface InfoPopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  body: string;
}
function InfoPopover({
  isOpen,
  onToggle,
  onClose,
  title,
  body
}: InfoPopoverProps) {
  return <span className="relative inline-flex" data-id="element-473" data-mp="gvaoep95fbyo">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={title} className="w-5 h-5 rounded-full bg-[#6C2BD9]/[0.08] hover:bg-[#6C2BD9]/[0.16] border border-[#6C2BD9]/30 text-[#6C2BD9] flex items-center justify-center transition-colors" data-id="element-474" data-mp="lrhow2irzv98">
        <Info className="w-3 h-3" data-id="element-475" data-mp="225hmh6u5f8tb" />
      </button>
      <AnimatePresence data-id="element-476" data-mp="228vlq67in1wj">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15
      }} className="absolute z-30 top-full left-0 mt-2 w-[280px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-477" data-mp="26736dc53ond0">
            <div className="flex items-start justify-between gap-3" data-id="element-478" data-mp="32zszy2zxshwg">
              <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-479" data-mp="2yy7is5gddhgg">
                {title}
              </p>
              <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-480" data-mp="2u1h4nno4kvxo">
                <X className="w-3.5 h-3.5" data-id="element-481" data-mp="s6owqu5rd5gk" />
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-482" data-mp="3l0x6hwenryxu">
              {body}
            </p>
          </motion.div>}
      </AnimatePresence>
    </span>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(2);
  const [keyCountries, setKeyCountries] = useState(1);
  const [secondaryCountries, setSecondaryCountries] = useState(0);
  const countries = keyCountries + secondaryCountries;
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [costBenchmarkOpen, setCostBenchmarkOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState<InfoKey>(null);
  // List-price unit economics
  const masterSnakCostBase = 2900;
  const masterSnakCostFloor = 1900;
  const campaignDuration = 3;
  const localizationCostBase = 999;
  const localizationCostSecondaryBase = 349;
  const monthlyInsightsFeeBase = 99;
  const monthlyInsightsFeeSecondaryBase = 39;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const effectiveSecondaryCountryCost = localizationCostSecondaryBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost * keyCountries + effectiveSecondaryCountryCost * secondaryCountries;
  const totalAnnualCampaigns = products * campaignsPerProduct;
  const MASTER_DISCOUNT_FLOOR = 1;
  const MASTER_DISCOUNT_CEILING = 100;
  const masterProgress = Math.min(1, Math.max(0, (totalAnnualCampaigns - MASTER_DISCOUNT_FLOOR) / (MASTER_DISCOUNT_CEILING - MASTER_DISCOUNT_FLOOR)));
  const effectiveMasterSnakCost = masterSnakCostBase - (masterSnakCostBase - masterSnakCostFloor) * masterProgress;
  const masterSnakCost = effectiveMasterSnakCost;
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const effectiveSecondaryMonthlyInsightsFee = monthlyInsightsFeeSecondaryBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee * keyCountries + effectiveSecondaryMonthlyInsightsFee * secondaryCountries;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-483" data-mp="dl84ha1wylor">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-484" data-mp="s9cqamw9jdbr">
        {/* Header */}
        <div className="mb-6" data-id="element-485" data-mp="naroij1d4ll7">
          <div className="flex items-center gap-3 mb-1" data-id="element-486" data-mp="nbkmauicg0r7">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-487" data-mp="3vqben96qlnw1">
              Snakker
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-488" data-mp="3qs1eek65nx3k">
            See for yourself — move the sliders to see the{' '}
            <span className="text-[#6C2BD9]" data-id="element-489" data-mp="4k1m0vfpf0sv">potential</span>
          </h1>


          {/* Industry benchmark + Cost benchmark popovers */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-490" data-mp="1jzqutsidzdar">
            <div className="relative inline-block" data-id="element-491" data-mp="1fzgqsle5mwyj">
              <button type="button" onClick={() => {
              setBenchmarkOpen(v => !v);
              setCostBenchmarkOpen(false);
            }} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-492" data-mp="1fyxa1808qem3">
                <Info className="w-3.5 h-3.5" data-id="element-493" data-mp="3vrb1fegwrqzt" />
                Industry benchmark
              </button>
              <AnimatePresence data-id="element-494" data-mp="3qqdmdar73d2j">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-495" data-mp="3mp1sgt1ag5oj">
                    <div className="flex items-start justify-between gap-3" data-id="element-496" data-mp="14jqzwv43c7uz">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-497" data-mp="14lv7bn177p00">
                        Big Pharma at a glance
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-498" data-mp="1egdh99x7kgs9">
                        <X className="w-3.5 h-3.5" data-id="element-499" data-mp="2twvs4unwmuly" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-500" data-mp="uw1oreg1snec">
                      Big pharma typically runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-501" data-mp="13udbh5dsn11i">
                        20–60 brands
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-502" data-mp="ytftotwp490j">
                        100–135 countries
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-503" data-mp="75xlj86rupnv">
                        4–6 campaigns/year
                      </span>
                      .
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="relative inline-block" data-id="element-504" data-mp="3ojt13x1a0jpk">
              <button type="button" onClick={() => {
              setCostBenchmarkOpen(v => !v);
              setBenchmarkOpen(false);
            }} aria-expanded={costBenchmarkOpen} className="hidden items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-505" data-mp="3jlrh99w10gd1">
                <Euro className="w-3.5 h-3.5" data-id="element-506" data-mp="1v2dhgsknsq92" />
                Cost benchmark
              </button>
              <AnimatePresence data-id="element-507" data-mp="2ishol9x2384t">
                {costBenchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[420px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-508" data-mp="2ms07k05bh950">
                    <div className="flex items-start justify-between gap-3" data-id="element-509" data-mp="13wrffbn613rj">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-510" data-mp="20mh7c945v7h3">
                        Cost Benchmark — Digital Pharma Campaigns
                      </p>
                      <button type="button" onClick={() => setCostBenchmarkOpen(false)} aria-label="Close cost benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-511" data-mp="1rl0xc2h9d7de">
                        <X className="w-3.5 h-3.5" data-id="element-512" data-mp="b6riq02tiwzr" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-513" data-mp="145f7wmjnzof">
                      A typical digital campaign for a legacy brand in one
                      country runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-514" data-mp="b10fajygu84t">
                        €15,000 – €19,000
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-515" data-mp="ju3mwbmfjqrj">
                      Compare against Snakker's cost per localized deployment.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-516" data-mp="yr4co9l29bnd">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-517" data-mp="2vjwdup8kihq8">
            <button type="button" onClick={() => setUnitsOpen(v => !v)} aria-expanded={unitsOpen} className="w-full flex items-center gap-3 rounded-2xl border-2 border-[#6C2BD9]/20 bg-white px-5 py-4 text-left hover:bg-[#6C2BD9]/[0.04] transition-colors" data-id="element-518" data-mp="2vkz9tbkqzad0">
              <SlidersHorizontal className="w-5 h-5 text-[#6C2BD9] shrink-0" data-id="element-519" data-mp="32fpk384x8ge1" />
              <span className="flex-1 flex flex-wrap items-baseline gap-x-2" data-id="element-520" data-mp="32dmq6d0ne341">
                <span className="text-base md:text-lg font-bold text-slate-900 tracking-tight" data-id="element-521" data-mp="2xf1osa9ntde0">
                  Unit economics
                </span>
                <span className="text-sm text-slate-400" data-id="element-522" data-mp="36huewa73vj29">
                  Fixed Snakker pricing behind the numbers
                </span>
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${unitsOpen ? 'rotate-180' : ''}`} data-id="element-523" data-mp="3l8a03oziur0f" />
            </button>

            <AnimatePresence initial={false} data-id="element-524" data-mp="3u7exgrzckeb0">
              {unitsOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="overflow-hidden" data-id="element-525" data-mp="3q6bkcd8oowdh">
                  <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-526" data-mp="3q1jl8p61730t">
              {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-527" data-mp="3h2ehn4fm4jhs">
                <div className="flex items-center justify-between" data-id="element-528" data-mp="3h0ldbvsh2bcy">
                  <div className="flex items-center gap-2" data-id="element-529" data-mp="3l4ja3zubra2u">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-530" data-mp="373p3nmqu7j6t">
                      Master Snak
                    </p>
                    <InfoPopover isOpen={infoOpen === 'master'} onToggle={() => setInfoOpen(infoOpen === 'master' ? null : 'master')} onClose={() => setInfoOpen(null)} title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-531" data-mp="3c20e9zoyhxq3" />
                  </div>
                  <div className="text-right" data-id="element-532" data-mp="33vidv6god2q7">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-533" data-mp="33tnsjr1hr8ll">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-534" data-mp="2pvpszrrtxgq5">
                  Per campaign master creation
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-535" data-mp="2kxxps1lqi86n">
                  Volume benefit: drops from €2,900 to €1,900 as total campaigns
                  / year scales from 1 to 100+.
                </p>
              </div>

              {/* Cost per country localization — tiered */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-536" data-mp="1aecmxxchluj3">
                <div className="flex items-center gap-2 mb-3" data-id="element-537" data-mp="1edlk7c6qkaje">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-538" data-mp="hom9lqioiwo1">
                    Cost per country localization
                  </p>
                  <InfoPopover isOpen={infoOpen === 'localization'} onToggle={() => setInfoOpen(infoOpen === 'localization' ? null : 'localization')} onClose={() => setInfoOpen(null)} title="What is Localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-539" data-mp="mo8oe4e5okyy" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-540" data-mp="3bgxrz0gwttaa">
                  <div className="rounded-lg bg-[#6C2BD9]/[0.06] px-3 py-2.5" data-id="element-541" data-mp="3fjkovfsfu4ec">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#6C2BD9]" data-id="element-542" data-mp="3kj6ylf9252ie">
                      Key markets
                    </p>
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight mt-1" data-id="element-543" data-mp="2nuhi9td2whyj">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                    <p className="text-[11px] text-slate-400" data-id="element-544" data-mp="2wtcmizwkbhz7">
                      Full-service adaptation
                    </p>
                  </div>
                  <div className="rounded-lg bg-teal-500/[0.08] px-3 py-2.5" data-id="element-545" data-mp="31m5urxr8p8g1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-teal-700" data-id="element-546" data-mp="244nkmynon8me">
                      Secondary / legacy
                    </p>
                    <p className="text-2xl font-bold text-teal-700 tracking-tight mt-1" data-id="element-547" data-mp="21kr9snfeu5fr">
                      €{Math.round(effectiveSecondaryCountryCost)}
                    </p>
                    <p className="text-[11px] text-slate-400" data-id="element-548" data-mp="1rm1rwunikkzi">
                      Template-led, no local effort
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-3 leading-relaxed" data-id="element-549" data-mp="1iln0renvcbo3">
                  Volume benefit applies to both tiers: 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-550" data-mp="18tqz5tmkq3f4">
                <div className="flex items-center justify-between" data-id="element-551" data-mp="13yk41a7de71t">
                  <div className="flex items-center gap-2" data-id="element-552" data-mp="zu40hfl8bbpr">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-553" data-mp="uw2kj3j3nsii">
                      Country activation and insights
                    </p>
                    <InfoPopover isOpen={infoOpen === 'insights'} onToggle={() => setInfoOpen(infoOpen === 'insights' ? null : 'insights')} onClose={() => setInfoOpen(null)} title="What are Data Insights?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper." data-id="element-554" data-mp="l6vj1x1bua5c" />
                  </div>
                  <div className="text-right" data-id="element-555" data-mp="u8bum83voib5">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-556" data-mp="u7af9rftx9nm">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-557" data-mp="1mx0mrhmyhdar">
                        / month
                      </span>
                    </p>
                    <p className="text-sm font-bold text-teal-700 tracking-tight" data-id="element-558" data-mp="3ceo42r4rvwuc">
                      €{effectiveSecondaryMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-559" data-mp="37fky94gyxmlb">
                        / month · secondary
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-560" data-mp="3h80h0403af79">Per country</p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-561" data-mp="2tgbskyqe2avl">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Portfolio Scale */}
            <div className="bg-[#6C2BD9]/[0.05] rounded-2xl p-5 space-y-5 border border-[#6C2BD9]/15" data-id="element-562" data-mp="hp7se5s1djye">
              <div data-id="element-563" data-mp="cqmvaf82zge6">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-564" data-mp="ctir9y0dbuzz">
                  Portfolio Scale
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-565" data-mp="1d061t9gbkrla">
                  Move the sliders — revenue updates instantly.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-566" data-mp="1i250i61470bw" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={2} max={12} data-id="element-567" data-mp="1vzjvrre1u2bs" />
              <SliderInput label="Key markets" value={keyCountries} onChange={setKeyCountries} min={0} max={30} data-id="element-568" data-mp="25pmpxuwwpmbp" />
              <SliderInput label="Secondary / legacy markets" value={secondaryCountries} onChange={setSecondaryCountries} min={0} max={100} data-id="element-569" data-mp="2er2xzw091ccr" />
              <div className="rounded-xl bg-white border border-[#6C2BD9]/15 px-4 py-3" data-id="element-570" data-mp="3v8l8awgsazip">
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#6C2BD9]" data-id="element-571" data-mp="3q7ns2pfc4fju">
                  One global PO
                </p>
                <p className="text-[12px] text-slate-500 leading-relaxed mt-1" data-id="element-572" data-mp="mr6w2ooejz27">
                  The whole secondary tier is invoiced centrally on a single
                  purchase order. Smaller markets never raise a local PO, never
                  onboard a vendor and never spend affiliate hours — adding a
                  market is a decision, not a procurement project.
                </p>
              </div>
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-573" data-mp="10qgc9oclyt66">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-574" data-mp="2l7pif6xj1mni">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-575" data-mp="2q5r3tyifdy25">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-576" data-mp="2gagxwzowp6j3">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-577" data-mp="278pvc3pnxftg">
                per localized deployment
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-578" data-mp="g6w5cedzclr">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-579" data-mp="5gl3es85yiiq">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-580" data-mp="amzq27ui6jxc">
                <div data-id="element-581" data-mp="ak3v8sdycbd4">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-582" data-mp="odcyscf7y2nq">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-583" data-mp="jltu2ja5yckx">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-584" data-mp="tcoinuavto9j">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-585" data-mp="td7qujaqcyad">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-586" data-mp="17bxlztdzonw3">
                    localized deployments / year
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-center justify-between gap-4 min-h-[36px]" data-id="element-587" data-mp="394vka4qjc8of">
                {costOpen && <p className="text-xs text-slate-500" data-id="element-588" data-mp="354lbylf9t66e">Estimated cost</p>}
                <div className="ml-auto flex items-center gap-3" data-id="element-589" data-mp="3tlcl3f41u7xm">
                  {costOpen && <motion.p initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-590" data-mp="2ejaxlra2ygy">
                      {formatEuro(calc.totalAnnualCost)}
                      <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-591" data-mp="3jopzgo3wfcuj">
                        / year
                      </span>
                    </motion.p>}
                  <button type="button" onClick={() => setCostOpen(v => !v)} aria-pressed={costOpen} aria-label={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} title={costOpen ? 'Hide estimated cost' : 'Reveal estimated cost'} className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${costOpen ? 'text-[#6C2BD9] bg-[#6C2BD9]/[0.08]' : 'text-slate-300 hover:text-[#6C2BD9] hover:bg-[#6C2BD9]/[0.08]'}`} data-id="element-592" data-mp="yza4dgxnd1z3">
                    <Euro className="w-3.5 h-3.5" data-id="element-593" data-mp="qc7f9xxjkuy8" />
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-594" data-mp="lccso6ksuriz">
              Key markets are priced for full-service adaptation and funded
              locally. Secondary, legacy and low-attention markets are priced to
              be viable at scale and funded centrally — so the long tail finally
              gets campaigns instead of waiting for local capacity.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-595" data-mp="2x0cxllcxwjae"></div>
      </div>
    </div>;
}